import type { SegmentRule, VoterDataPurposeDto } from '@signalvote/shared';
import { Confidence, type Estimate } from './voterEstimate';
import { ListPurpose, assertNever } from '@signalvote/shared';
import { formatCount } from './VoterDataFieldPopover';

/**
 * A canvass shift knocks about this many doors: three hours at roughly twenty an hour. A planning
 * figure for reading a door count as shifts, not a promise.
 */
const DOORS_PER_CANVASS_SHIFT = 60;

/** A rough USPS Marketing Mail letter rate, in cents, for reading a mail list as postage. An estimate, not a quote. */
const POSTAGE_CENTS_PER_PIECE = 40;

const sameValues = (a: string[], b: string[]) => a.length === b.length && a.every((v) => b.includes(v));

/** Whether `rule` is exactly one of `purpose`'s defaults. */
export const isDefaultOf = (purpose: VoterDataPurposeDto | undefined, rule: SegmentRule) =>
  rule.kind === 'Values' && (purpose?.defaultRules.some((d) => d.fieldId === rule.fieldId && sameValues(d.values, rule.values)) ?? false);

/**
 * The rules after switching purpose from `from` to `to`. `defaultIds` are the ids of rules a
 * purpose added. The customer's rules are kept, even one that happens to match a purpose's
 * default; a rule the old purpose added that the customer never changed is dropped, since it came
 * with that purpose, not from them; and the new purpose's defaults are added for fields with no
 * rule yet, as many as fit in `room` rules (L2's filter cap less the area). Returns the rules, the
 * ids of the defaults just added and the defaults left out for want of room.
 */
export function applyPurpose(
  rules: SegmentRule[],
  defaultIds: ReadonlySet<string>,
  from: VoterDataPurposeDto | undefined,
  to: VoterDataPurposeDto,
  newId: () => string,
  room = Infinity,
): { rules: SegmentRule[]; defaultIds: Set<string>; leftOut: VoterDataPurposeDto['defaultRules'] } {
  const untouchedDefault = (rule: SegmentRule) => defaultIds.has(rule.id) && isDefaultOf(from, rule);
  const kept = rules.filter((rule) => !untouchedDefault(rule));
  const wanted = to.defaultRules.filter((d) => !kept.some((rule) => rule.fieldId === d.fieldId));
  const fits = Math.max(0, room - kept.length);
  const added = wanted
    .slice(0, fits)
    .map((d): SegmentRule => ({ id: newId(), fieldId: d.fieldId, kind: 'Values', values: [...d.values] }));
  return { rules: [...kept, ...added], defaultIds: new Set(added.map((r) => r.id)), leftOut: wanted.slice(fits) };
}

/** Whole dollars when asked, except under a dollar, which would round to "$0": that shows its cents. */
export const formatDollars = (cents: number, wholeDollars = false) => {
  const digits = wholeDollars && cents >= 100 ? 0 : 2;
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/** "1 voter", "2 voters": the noun for a count. */
export const nounFor = (count: number, noun: string) => (count === 1 ? noun : `${noun}s`);

/**
 * The count in the purpose's own terms: doors and canvass shifts, dials, or postage, and whether
 * the count it comes from is an estimate. Null for a purpose whose voters are the natural unit.
 */
export function purposeTerms(
  purpose: ListPurpose,
  counts: { households: Estimate; records: Estimate },
): { text: string; estimated: boolean } | null {
  const termsOf = (count: Estimate, text: (value: number, about: string) => string) => {
    const estimated = count.confidence !== Confidence.Exact;
    return { text: text(count.value, estimated ? 'about ' : ''), estimated };
  };
  switch (purpose) {
    case ListPurpose.Doors:
      return termsOf(counts.households, (doors, about) => {
        const shifts = Math.ceil(doors / DOORS_PER_CANVASS_SHIFT);
        return `${about}${formatCount(doors)} ${nounFor(doors, 'door')} · about ${formatCount(shifts)} ${nounFor(shifts, 'canvass shift')}`;
      });
    case ListPurpose.Phones:
      return termsOf(counts.records, (dials, about) => `${about}${formatCount(dials)} ${nounFor(dials, 'dial')}`);
    case ListPurpose.Mail:
      return termsOf(counts.records, (pieces) => `About ${formatDollars(pieces * POSTAGE_CENTS_PER_PIECE, true)} in postage`);
    case ListPurpose.Turnout:
      return null;
    default:
      return assertNever(purpose);
  }
}
